import React from "react";
import { useNavigate } from "react-router-dom";
import { useGetApplicationsQuery } from "@/store/service";
import { useAuth } from "@/context/AuthContext";
import { getUrlStepFromApplicationStep } from "@/util/url-utils";
import { FileText, Plus, ArrowRight, Shield } from "lucide-react";

const applicationTypeLabels: Record<string, string> = {
  HomeownerAssistant: "Homeowner Assistant",
  HomeownershipAccelerator: "Homeownership Accelerator",
  UpgradeAccelerator: "Upgrade Accelerator",
};

const Dashboard = () => {
  const { user: userData } = useAuth();
  const { data: applicationData, isLoading } = useGetApplicationsQuery();
  const navigate = useNavigate();

  if (!userData) {
    return null;
  }

  const isAdmin = userData.roles?.includes("ROLE_ADMIN");
  const applications = applicationData || [];

  return (
    <div className="w-full max-w-4xl mx-auto py-8 px-4">
      <div className="mb-8">
        <h1 className="text-2xl font-semibold text-gray-900">Welcome back, {userData.firstName || userData.email}</h1>
        <p className="text-gray-500 mt-1">Manage your applications and account.</p>
      </div>

      <div className="flex flex-wrap gap-3 mb-8">
        <button
          onClick={() => navigate("/upfront-boost/register")}
          className="inline-flex items-center gap-2 px-4 py-2.5 rounded-lg bg-gray-900 text-white text-sm font-medium hover:bg-gray-800 transition-colors cursor-pointer"
        >
          <Plus className="size-4" /> New Application
        </button>
        {isAdmin && (
          <button
            onClick={() => navigate("/admin")}
            className="inline-flex items-center gap-2 px-4 py-2.5 rounded-lg bg-purple-600 text-white text-sm font-medium hover:bg-purple-500 transition-colors cursor-pointer"
          >
            <Shield className="size-4" /> Admin Portal
          </button>
        )}
      </div>

      <div>
        <h2 className="text-lg font-semibold text-gray-900 mb-4">Your Applications</h2>

        {isLoading && <div className="text-center py-12 text-gray-500">Loading applications...</div>}

        {!isLoading && applications.length === 0 && (
          <div className="bg-white rounded-xl border border-gray-200 p-12 text-center">
            <FileText className="size-10 text-gray-300 mx-auto mb-3" />
            <p className="text-gray-500 mb-4">You don't have any applications yet.</p>
            <button
              onClick={() => navigate("/upfront-boost")}
              className="inline-flex items-center gap-2 px-5 py-2.5 rounded-lg bg-[#9BFBE0] text-sm font-medium text-gray-900 hover:shadow-md transition-all cursor-pointer"
            >
              Get Started <ArrowRight className="size-4" />
            </button>
          </div>
        )}

        {!isLoading && applications.length > 0 && (
          <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
            <table className="w-full">
              <thead>
                <tr className="border-b border-gray-200 bg-gray-50">
                  <th className="text-left px-4 py-3 text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Type
                  </th>
                  <th className="text-left px-4 py-3 text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Status
                  </th>
                  <th className="text-left px-4 py-3 text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Current Step
                  </th>
                  <th className="text-left px-4 py-3 text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Created
                  </th>
                  <th className="text-right px-4 py-3 text-xs font-medium text-gray-500 uppercase tracking-wider"></th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {applications.map((app) => (
                  <tr
                    key={app.id}
                    className="hover:bg-gray-50 transition-colors cursor-pointer"
                    onClick={() => navigate(`/application/${app.id}/${getUrlStepFromApplicationStep(app.step)}`)}
                  >
                    <td className="px-4 py-3 text-sm text-gray-900 font-medium">
                      {applicationTypeLabels[app.type] || app.type}
                    </td>
                    <td className="px-4 py-3">
                      <span
                        className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${
                          app.status === "Complete"
                            ? "bg-green-100 text-green-700"
                            : app.status === "Incomplete"
                              ? "bg-yellow-100 text-yellow-700"
                              : "bg-gray-100 text-gray-700"
                        }`}
                      >
                        {app.status}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-sm text-gray-600">
                      {app.step?.replace(/-/g, " ").replace(/\b\w/g, (c) => c.toUpperCase()) || "—"}
                    </td>
                    <td className="px-4 py-3 text-sm text-gray-500">
                      {app.createdAt ? new Date(app.createdAt).toLocaleDateString() : "—"}
                    </td>
                    <td className="px-4 py-3 text-right">
                      <span className="inline-flex items-center gap-1 text-sm font-medium text-gray-900">
                        Continue <ArrowRight className="size-3.5" />
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
};

export default Dashboard;
